import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { IndianRupee, Clock, Receipt, Download } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const kindLabel = (k) => k === "plot_purchase" ? "Plot purchase" : "Maintenance";

/** Customer-facing card: shows upcoming dues + recent payment history. */
export function DuesAndPaymentsCard() {
  const [dues, setDues] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/maintenance-invoices?status=due").then((r) => setDues(r.data)).catch(() => setDues([])),
      api.get("/payments").then((r) => setPayments(r.data)).catch(() => setPayments([])),
    ]);
  }, []);

  const totalDue = dues.reduce((s, d) => s + Number(d.amount || 0), 0);

  return (
    <div className="border border-border rounded-lg bg-card p-5" data-testid="dues-and-payments-card">
      <div className="flex items-center gap-2 mb-4">
        <IndianRupee size={14} className="text-primary" />
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground">Payments</div>
      </div>

      {dues.length > 0 ? (
        <div className="mb-5 rounded-md border border-destructive/30 bg-destructive/5 p-3" data-testid="dues-panel">
          <div className="flex items-center gap-2 text-destructive font-semibold text-sm">
            <Clock size={13} /> {dues.length} outstanding due{dues.length === 1 ? "" : "s"} · {fmt(totalDue)}
          </div>
          <ul className="mt-2 space-y-1 text-xs">
            {dues.slice(0, 4).map((d) => (
              <li key={d.id} className="flex justify-between" data-testid={`due-${d.id}`}>
                <span>Period {d.period_start} → {d.period_end}</span>
                <span className="font-medium">{fmt(d.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mb-5 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-primary" data-testid="dues-clear">
          All clear — no maintenance dues right now.
        </div>
      )}

      <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
        <Receipt size={11} /> Recent receipts
      </div>
      {payments.length === 0 ? (
        <div className="text-xs text-muted-foreground" data-testid="no-payments">No payments recorded yet.</div>
      ) : (
        <ul className="divide-y divide-border">
          {payments.slice(0, 5).map((p) => (
            <li key={p.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`payment-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{fmt(p.amount)} <span className="text-[10px] uppercase tracking-widest text-primary font-semibold ml-1">{kindLabel(p.kind)}</span></div>
                <div className="text-xs text-muted-foreground">{p.paid_at} · {p.method?.replace("_", " ")}{p.reference ? ` · ${p.reference}` : ""}</div>
              </div>
              <a href={`${backend}/api/payments/${p.id}/receipt`} target="_blank" rel="noreferrer"
                 className="text-primary text-xs inline-flex items-center gap-1"
                 data-testid={`receipt-link-${p.id}`}>
                <Download size={12} /> PDF
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
